import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import FileUploadField from '../base/FileUploadField';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { useUpdateStep } from '../../hooks/useGuides';
import { AcceptedFileType, FileUploadTabType } from '../base/file-upload/utils';
import { FileMetadata } from '@/services/apis/files';
import { GuideStepType } from '@/services/apis/guides';
import StepEditorWrapper from './StepEditorWrapper';

// Convert YouTube URL to embed format
const getYouTubeEmbedUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    let videoId: string | null = null;

    if (urlObj.searchParams.has('v')) {
      videoId = urlObj.searchParams.get('v');
    } else if (urlObj.hostname === 'youtu.be') {
      videoId = urlObj.pathname.substring(1);
    } else if (urlObj.pathname.includes('/embed/')) {
      return url; // Already in embed format
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  } catch {
    return url;
  }
};

export const VideoStepView: React.FC<{ fileMetadata: FileMetadata; title?: string }> = ({ fileMetadata, title }) => {
  const isYouTube = fileMetadata?.provider === 'youtube';

  const videoUrl = fileMetadata?.url || '';
  const embedUrl = isYouTube ? getYouTubeEmbedUrl(videoUrl) : undefined;
  return (
    <>
      {isYouTube ? (
        <div className="w-full aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
          <iframe
            src={embedUrl}
            className="w-full h-full rounded-lg"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={title || 'YouTube video player'}
            key={embedUrl}
          />
        </div>
      ) : (
        <video title={title} controls className="w-full rounded-lg max-h-96" key={videoUrl}>
          <source src={videoUrl} type={fileMetadata.type} />
          Your browser does not support the video tag.
        </video>
      )}
    </>
  );
};

const VideoStepEditorForm: React.FC = () => {
  const {
    guide: { id: guideId },
    state: { selectedStep },
  } = useGuideBuilderGuide();
  const { t } = useTranslation('admin');
  const updateStep = useUpdateStep(guideId || '');
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [shouldAutoSave, setShouldAutoSave] = useState(false);

  // Initialize fileMetadata from step content
  useEffect(() => {
    const content = selectedStep?.content as any;
    if (content?.video && typeof content.video === 'object' && 'id' in content.video) {
      setFileMetadata(content.video as FileMetadata);
    } else {
      setFileMetadata(null);
    }
    setFile(null);
    setShouldAutoSave(false);
  }, [selectedStep]);

  const handleFileUploaded = (metadata: FileMetadata) => {
    setFileMetadata(metadata);
    setFile(null);
    // Trigger auto-save after file upload
    setShouldAutoSave(true);
  };

  const handleRemove = () => {
    setFileMetadata(null);
    setFile(null);
  };

  const handleSave = async () => {
    if (!selectedStep?.id || !fileMetadata) return;

    try {
      await updateStep.mutateAsync({
        stepId: selectedStep.id,
        data: {
          content: {
            video: fileMetadata,
          },
        },
      });
    } catch (error) {
      console.error('Error saving video step:', error);
    }
  };

  const handleAutoSaveComplete = useCallback(() => {
    setShouldAutoSave(false);
  }, []);

  if (!selectedStep) {
    return null;
  }

  return (
    <StepEditorWrapper
      title={t('guides.builder.stepEditor.videoStep') || 'Video Step'}
      stepType={GuideStepType.VIDEO}
      onSave={handleSave}
      isSaveDisabled={!fileMetadata || updateStep.isPending}
      isPending={updateStep.isPending}
      shouldAutoSave={shouldAutoSave}
      onAutoSaveComplete={handleAutoSaveComplete}
    >
      {fileMetadata ? (
        <div className="space-y-4">
          <div className="relative">
            <VideoStepView fileMetadata={fileMetadata} />
            <Button
              isIconOnly
              color="danger"
              variant="solid"
              size="sm"
              className="absolute top-2 right-2"
              onPress={handleRemove}
              aria-label={t('common.remove') || 'Remove video'}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-foreground font-medium mb-1">{fileMetadata.name}</p>
            <p className="text-xs text-muted-foreground">
              {fileMetadata.provider === 'youtube' ? 'YouTube' : fileMetadata.type} •{' '}
              {fileMetadata.size > 0 ? `${(fileMetadata.size / 1024 / 1024).toFixed(2)} MB` : 'External URL'}
            </p>
          </div>
        </div>
      ) : (
        <FileUploadField
          file={file}
          onChange={setFile}
          acceptedTypes={[AcceptedFileType.VIDEO, AcceptedFileType.YOUTUBE]}
          shownTabs={[FileUploadTabType.FILE_UPLOAD, FileUploadTabType.EXTERNAL_URL, FileUploadTabType.MEDIA_LIBRARY]}
          onFileUploaded={handleFileUploaded}
          maxSize={500}
        />
      )}
    </StepEditorWrapper>
  );
};

export default VideoStepEditorForm;
